import { useAppDispatch } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import type { GrammarResponseSlideData } from "@ai-teach/types";
import type { MediaInput, SlideHandlers } from "../types";

/**
 * Handlers for grammar - response
 *
 */
export const useGrammarResponseHandlers = (slide: SlideFields | null): SlideHandlers => {
  const dispatch = useAppDispatch();

  const slideData = slide?.slideData as GrammarResponseSlideData | undefined;

  if (!slide || !slideData) {
    // Return no-op functions if no slide data
    return {
      addVideo: () => {},
      addAudio: () => {},
      addImage: () => {},
    };
  }

  const slideId = slide.id;

  /**
   * Add video to grammar-response slide
   * Updates the video property in slideData
   */
  const addVideo = ({ src, id, filename }: MediaInput) => {
    dispatch(
      updateSlide({
        slideId,
        // Drop the old video's subtitles; SubtitleSection regenerates.
        slideData: { ...slideData, video: { src, id, filename }, subtitles: [] },
      } as {
        slideId: string;
        slideData: GrammarResponseSlideData;
      })
    );
  };

  /**
   * Add audio to grammar-response slide
   * Updates the audio property in slideData
   */
  const addAudio = ({ src, id, filename }: MediaInput) => {
    dispatch(
      updateSlide({ slideId, slideData: { ...slideData, audio: { src, id, filename } } } as {
        slideId: string;
        slideData: GrammarResponseSlideData;
      })
    );
  };

  const addImage = ({ src, id, filename }: MediaInput, questionIndex: string | undefined) => {
    if (!questionIndex || !slideData?.checkpoints) {
      throw new Error("Drag image to one of the checkpoint!");
    }

    dispatch(
      updateSlide({
        slideId,
        slideData: {
          ...slideData,
          checkpoints: slideData.checkpoints.map((p, index) =>
            index === Number(questionIndex) ? { ...p, image: { src, filename, id } } : p
          ),
        } as GrammarResponseSlideData,
      })
    );
  };

  return { addVideo, addAudio, addImage };
};
